// The EasyPlay 1s connection for Jianpu Studio. Wraps the shared Web MIDI
// hook and does the movable-do translation: the instrument's transpose dial
// means incoming MIDI notes are absolute pitches, so we keep a calibrated
// "tonic" (the MIDI note the ① button sends) and hand pages scale degrees.
//
//   const ep = useEasyPlay({ onNote, enabled });
//   ep.supported / ep.connected / ep.deviceNames  — Web MIDI status
//   ep.tonicMidi                                  — current calibration
//   ep.calibrating / ep.startCalibration() / ep.cancelCalibration()
//   ep.setTonicFromKey("G")                       — dial-position fallback
//   ep.pressed                                    — Set of "degree:octave" held
//
// onNote({ midi, degree, octave, accidental, velocity, timeMs }) fires for
// every note-on (except the one consumed by calibration); onNoteOff gets
// { midi, timeMs } when it lifts. timeMs is performance.now() at arrival.

import { useCallback, useRef, useState } from "react";
import useHomeKeyMidi from "../homeKey/useHomeKeyMidi";
import { midiToDegree, keyTonicMidi, noteKey } from "../../lib/jianpu";

const TONIC_KEY = "jianpu-studio:tonic:v1";

function loadTonic() {
  try {
    const raw = window.localStorage.getItem(TONIC_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n >= 12 && n <= 108 ? n : 60;
  } catch {
    return 60;
  }
}

function storeTonic(midi) {
  try {
    window.localStorage.setItem(TONIC_KEY, String(midi));
  } catch {
    // best-effort
  }
}

export default function useEasyPlay({ onNote, onNoteOff, enabled = true } = {}) {
  const [tonicMidi, setTonicMidi] = useState(loadTonic);
  const [calibrating, setCalibrating] = useState(false);
  const [pressed, setPressed] = useState(() => new Set());
  const onNoteRef = useRef(onNote);
  onNoteRef.current = onNote;
  const onNoteOffRef = useRef(onNoteOff);
  onNoteOffRef.current = onNoteOff;
  const calibratingRef = useRef(false);
  calibratingRef.current = calibrating;
  const tonicRef = useRef(tonicMidi);
  tonicRef.current = tonicMidi;

  const handleNoteOn = useCallback((midi, velocity) => {
    if (calibratingRef.current) {
      setTonicMidi(midi);
      storeTonic(midi);
      setCalibrating(false);
      return;
    }
    const spelled = midiToDegree(midi, tonicRef.current);
    setPressed((prev) => {
      const next = new Set(prev);
      next.add(noteKey(spelled));
      return next;
    });
    if (onNoteRef.current)
      onNoteRef.current({ midi, ...spelled, velocity, timeMs: performance.now() });
  }, []);

  const handleNoteOff = useCallback((midi) => {
    const spelled = midiToDegree(midi, tonicRef.current);
    setPressed((prev) => {
      const next = new Set(prev);
      next.delete(noteKey(spelled));
      return next;
    });
    if (onNoteOffRef.current) onNoteOffRef.current({ midi, timeMs: performance.now() });
  }, []);

  const midi = useHomeKeyMidi(handleNoteOn, handleNoteOff, enabled);

  const startCalibration = useCallback(() => setCalibrating(true), []);
  const cancelCalibration = useCallback(() => setCalibrating(false), []);
  const setTonicFromKey = useCallback((key) => {
    const m = keyTonicMidi(key);
    setTonicMidi(m);
    storeTonic(m);
  }, []);

  return {
    ...midi, // supported, connected, deviceNames
    tonicMidi,
    calibrating,
    startCalibration,
    cancelCalibration,
    setTonicFromKey,
    pressed,
  };
}
